﻿import { IInputScope, KbInput } from "@app/components/kb-input/kb-input.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { SearchUtils, Utils } from "@tools";
import * as angular from "angular";

export interface IMultiSelectScope extends IInputScope {
    // external members
    ngModel: any[];
    source: any[];

    /**
     * 'objects' or 'values'.  'objects' is the default
     * In objects mode, when a source item is selected, the whole object will be added to the ngModel array.
     * In values mode, only the value of the source item will be added to the ngModel array.
     * NOTE: the mode is overriden when addItem delegate is used.  
     * AddItem gives you control over exactly how to add the item.
     */
    mode: string;
    /**
     * leave valueField empty to treat the whole option item as the value
     */
    valueField: string;
    labelField: string;

    headerTemplate: string;
    allowDeleteInHeader: boolean;
    itemIsSelectable: (item: any) => boolean;
    /**
     * custom data passed in so that header templates can access things in the outer scope
     */
    data: any;
    /**
     * Take manual control of adding an item.
     */
    addItem: (sourceItem: any) => void;
    /**
     * take manual control of the removal of an item
     */
    removeItem: (item: any) => void;
    ngDisabled: boolean;
    itemStyle: any;
    mobileDropdown: boolean;

    // internal directive members
    _selectedItems: any[];
    /** the option currently highlighted in the dropdown (not necessarily the same as the selectedOption) */
    _activeIndex: number;
    _select: (item: any) => void;
    _click: (e: ng.IAngularEvent) => void;
    _dropdownOpen: boolean;
    _isEnabled: () => boolean;
    _itemDeleteBtnClick: (e: ng.IAngularEvent, item: any) => void;
    _delete: (item: any) => void;
    _isActive: (index) => boolean;
    _selectedMap: boolean[];
    _getItemLabel: (item: any) => string;
    _id: string;

    _binding: { query: string };
    _filteredSource: any[];
    _queryChange: (e: ng.IAngularEvent) => void;
    _queryKeydown: (e: JQueryEventObject) => void;
    _itemClick: (e: JQueryEventObject, item: any) => void;
    _itemMousedown: (e: JQueryEventObject) => void;
    _itemIsSelectable: (item: any) => boolean;
}

@NgComponent({
    token: Token.KbMultiSelect,
    dependencies: [
        Token.$Timeout,
        Token.$Window,
    ]
})
export class KbMultiSelect extends KbInput {
    constructor(
        $timeout: ng.ITimeoutService,
        $window: ng.IWindowService
    ) {
        super($timeout);

        this.template = Dirs.component("kb-multi-select");
        this.scope = Utils.extend(this.scope, {
            source: "=",
            valueField: "@",
            labelField: "@",
            headerTemplate: "@",
            data: "=?",
            addItem: "&",
            removeItem: "&",
            mode: "@",
            itemStyle: "=?",
            allowDeleteInHeader: "=?",
            itemIsSelectable: "&",
            mobileDropdown: "=?"
        });
        this.transclude = true;

        this.compile = (element, atts: any) => {
            if (angular.isUndefined(atts.mode)) {
                atts.mode = "objects";
            }
            if (angular.isUndefined(atts.allowDeleteInHeader)) {
                atts.allowDeleteInHeader = "true";
            }
            if (atts.mobileDropdown == null) atts.mobileDropdown = "true";

            return this.link;
        };
        this.link = (s, e, a, c) => this.kbMultiSelectLinkFn(s as IMultiSelectScope, e, a, c, true);
    }
    public kbMultiSelectLinkFn(scope: IMultiSelectScope, element: JQuery, atts: any, controllers: any[], shouldFloat: boolean = true) {
        this.kbInputLinkFn(scope, element, atts, controllers, shouldFloat);
        let ngModelCtrl = controllers[0] as ng.INgModelController;

        scope._dropdownOpen = false;
        scope._selectedItems = [];
        scope._selectedMap = [];
        scope._binding = { query: "" };
        let input = element.find("input");
        scope._id = Utils.shortId();

        scope._isEnabled = () => {
            return !scope.ngDisabled;
        };

        let getItemValue = (item: any) => {
            if (item == null) return null;
            return scope.valueField ? item[scope.valueField] : item;
        };

        scope._getItemLabel = (item: any): string => {
            if (scope.labelField && item[scope.labelField]) {
                return item[scope.labelField];
            } else if (scope.valueField) {
                return item[scope.valueField];
            } else {
                return item;
            }
        };

        let updateSelected = () => {
            scope._selectedItems.clear();
            // set all options to visible
            scope._selectedMap.clear();
            if (scope.source) scope.source.forEach(item => scope._selectedMap.push(false));

            if (ngModelCtrl.$viewValue) {
                for (let item of ngModelCtrl.$viewValue) {
                    let itemValue = item;
                    if (scope.mode == "objects") {
                        itemValue = getItemValue(item);
                    }

                    if (scope.source) {
                        for (let i = 0; i < scope.source.length; i++) {
                            let sourceItem = scope.source[i];
                            if (getItemValue(sourceItem) == itemValue) {
                                scope._selectedItems.push(sourceItem);
                                // hide the option since it's selected
                                scope._selectedMap[i] = true;
                                break;
                            }
                        }
                    }
                }
            }
        };

        // let showDropdown = () => {
        //     scope._dropdownOpen = true;
        //     // on mobile the filter input will kick off the keyboard, which we don't necessarily want.  
        //     // The problem is worse on IOS because of focus restrictions
        //     if ($window.innerWidth >= Utils.MOBILE_WIDTH && !Utils.isIos()) {
        //         $timeout(() => input.focus());
        //     }
        // };

        // let hideDropdown = () => {
        //     scope._dropdownOpen = false;
        //     scope._binding.query = null;
        //     updateFilteredSource();
        // };
        // let toggleDropdown = () => {
        //     scope._dropdownOpen ? hideDropdown() : showDropdown();
        // };

        // options might be added after the ngModel is set by the consumer, so
        // we need to refind the selected option 
        scope.$watchCollection("source", () => {
            updateSelected();
            updateFilteredSource();
        });

        scope.$watchCollection("ngModel", () => {
            // ngModel watches by reference, which doesn't work well for arrays
            updateSelected();
        });

        ngModelCtrl.$render = () => {
            updateSelected();
        };

        scope._itemMousedown = (e) => {
            e.preventDefault(); // so we don't lose focus
        }
        scope._itemClick = (e, item) => {
            e.preventDefault(); 
            scope._select(item);
        };

        scope._itemIsSelectable = (item) => {
            if (atts.itemIsSelectable) {
                return scope.itemIsSelectable({ item });
            }
            return true;
        }
        
        scope._select = (sourceItem) => {
            if (scope._isEnabled() && scope._itemIsSelectable(sourceItem)) {
                if (atts.addItem) {
                    scope.addItem({ sourceItem });
                } else {
                    if (isSelected(sourceItem)) {
                        scope._delete(sourceItem);
                    } else {
                        let viewValue = ngModelCtrl.$viewValue as any[];
                        if (scope.mode == "objects") {                                
                            ngModelCtrl.$setViewValue(viewValue.concat([sourceItem])); // $viewValue.push(sourceItem);
                        } else {
                            ngModelCtrl.$setViewValue(viewValue.concat([getItemValue(sourceItem)]));
                        }
                    }
                }
                //updateSelected(); don't need to call because the watch on ngModel picks up the change
                //scope.ngChange();
                // moveDown();
                
            }
        };

        scope._isActive = index => {
            return index === scope._activeIndex;
        };

        let updateFilteredSource = () => {
            if (!scope._binding.query) {
                scope._filteredSource = scope.source;
            } else {
                scope._filteredSource = SearchUtils.search(
                    scope._binding.query,
                    scope.source,
                    o => scope._getItemLabel(o),
                    10000
                );
                scope._activeIndex = -1;
            }
        };

        scope._queryChange = e => {
            updateFilteredSource();
        };

        scope._queryKeydown = e => {
            if (e.which == 38) {
                moveUp();
                e.stopPropagation();
            }
            if (e.which == 40) {
                moveDown();
                e.stopPropagation();
            }
            if (e.which === 13) {
                enter();
                e.stopPropagation();
            }
            if (e.which === 9) {
                tab();
                if (scope._dropdownOpen) {
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
        };

        scope._click = e => {
            if (scope._isEnabled()) {
                scope._activeIndex = -1;
                scope._dropdownOpen = !scope._dropdownOpen;
            }
        };

        scope._itemDeleteBtnClick = (e, item) => {
            scope._delete(item);
            e.stopPropagation();
        };

        scope._delete = (item) => {
            if (scope._isEnabled()) {
                if (atts.removeItem) {
                    scope.removeItem({ item });
                } else {
                    let itemValue = getItemValue(item);
                    // the normal array.remove compares values strictly (with ===), 
                    // but we need == in this case because of possible string / number mismatch
                    for (let i = ngModelCtrl.$viewValue.length - 1; i >= 0; i--) {
                        let ngModelValue = ngModelCtrl.$viewValue[i];
                        if (scope.mode == "objects") {
                            ngModelValue = getItemValue(ngModelValue);
                        }
                        if (ngModelValue == itemValue) {
                            let viewValue = ngModelCtrl.$viewValue as any[];
                            let newVal = [...viewValue];
                            newVal.splice(i, 1);
                            ngModelCtrl.$setViewValue(newVal);
                            break;
                        }
                    }
                }
                //updateSelected(); don't need to call because the watch on ngModel picks up the change
                // scope.ngChange();
            }
        };

        let isSelected = sourceItem => {
            let optionVal = getItemValue(sourceItem);
            for (let selectedItem of scope._selectedItems) {
                if (optionVal === getItemValue(selectedItem)) {
                    return true;
                }
            }

            return false;
        };
        let moveUp = () => {
            if (scope._dropdownOpen) {
                let newIndex = scope._activeIndex - 1;
                if (newIndex >= 0) {
                    scope._activeIndex = newIndex;
                }
            }
        };

        let moveDown = () => {
            scope._dropdownOpen = true;
            let newIndex = scope._activeIndex + 1;
            if (newIndex < scope._filteredSource.length) {
                scope._activeIndex = newIndex;
            }
        };

        let enter = () => {
            if (scope._dropdownOpen) {
                if (scope._filteredSource.length && scope._activeIndex > -1) {
                    scope._select(scope.source[scope._activeIndex]);
                }
            } else {
                scope._dropdownOpen = true;
            }
        };

        let tab = () => {
            if (scope._dropdownOpen) {
                if (scope._filteredSource.length && scope._activeIndex > -1) {
                    scope._select(scope.source[scope._activeIndex]);
                    scope._dropdownOpen = false;
                }
            }
        };

        // bind keyboard events: arrows up(38) / down(40), enter(13) and tab(9), esc(27)
        element.on("keydown", (e: JQuery.Event) => {
            if (e.key == 'ArrowUp') {
                e.preventDefault();
                scope.$apply(() => {
                    moveUp();
                });
            }
            if (e.key == 'ArrowDown') {
                e.preventDefault();
                scope.$apply(() => {
                    moveDown();
                });
            }
            if (e.key == 'Enter') {
                scope.$apply(() => {
                    e.preventDefault();
                    enter();
                });
            }
            if (e.key == 'Tab') {
                scope.$apply(() => {
                    if (scope._dropdownOpen) {
                        e.stopPropagation();
                        e.preventDefault();
                    }
                    tab();
                });
            }
            if (e.key == 'Escape') {
                e.preventDefault();
                element.trigger('blur');
                scope.$apply(() => {
                    scope._dropdownOpen = false;
                    e.stopPropagation();
                });
            }
        });

        scope.$on("$destroy", () => {
            element.off();
        });
    }
}
